import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { AnonymousFeedbackForm } from "../components/Candidate-Sections/anonymous-feedback-form"
import { ProfileHeader } from "../components/Candidate-Sections/profile-header"
import { ProfileOverview } from "../components/Candidate-Sections/profile-overview"
import {
    fetchCandidateByFeedbackToken,
    formatCandidateCurrentLocation,
    type AnonymousFeedbackCandidateDetail,
} from "../features/jobs/jobAPI"

const formatEnumValue = (value?: string | null) => {
    if (!value) return "N/A"
    return value
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ")
}

const transformCandidateData = (data: AnonymousFeedbackCandidateDetail): any => {
    const profilePicture = data.profile_picture_url || data.profile_picture

    return {
        id: data.id,
        name: data.full_name,
        role: data.job_title || "N/A",
        experience: data.years_of_professional_experience?.name || "N/A",
        location: formatCandidateCurrentLocation(data.current_location),
        profile_picture: profilePicture,
        avatar: profilePicture || "/placeholder.svg",
        matchedSkills: 0,
        missingSkills: 0,
        score: Number(data.match_score ?? 0),
        expertise: data.areas_of_expertise?.map((area) => ({
            category: area.field_of_expertise?.name || "N/A",
            years: area.years_in_this_area_of_expertise || 0,
            healthcare: area.company?.map((c) => c.name).join(", ") || "N/A",
            locations: area.region?.map((r) => r.name).join(", ") || "N/A",
            mustHave: area.key_skills?.map((skill) => ({ name: skill.name, variant: "solid" as const })) || [],
            niceToHave: [],
        })) || [],
        aptitude: {
            percentile: 0,
            rawScore: 0,
            tests: [],
        },
        gender: data.gender === "M" ? "Male" : data.gender === "F" ? "Female" : "Other",
        dateOfBirth: data.date_of_birth ? new Date(data.date_of_birth).toLocaleDateString() : "N/A",
        email: data.email?.trim() || "",
        contactNumber: data.phone_number?.trim() || "N/A",
        visaStatus: formatEnumValue(data.preferred_locations?.[0]?.visa_status),
        drivingLicense: formatEnumValue(data.preferred_locations?.[0]?.driving_license),
        nationality: data.nationality?.name || "N/A",
        languages: data.language_skills?.map((lang) => ({
            lang: lang.language?.name || "N/A",
            level: lang.speaking_level || "N/A",
            skills: lang.reading_writing_level || "N/A",
        })) || [],
        education: data.education?.map((edu) => ({
            degree: `${edu.qualification?.name || ""} in ${edu.field_of_study?.name || ""}`.trim() || "N/A",
            university: edu.institution?.name || "N/A",
            years: edu.year_of_obtension?.toString() || "N/A",
        })) || [],
        certifications: [],
        teamExperience: data.manages_people ? "Yes" : "No",
    }
}

export default function AnonymousFeedback() {
    const [searchParams] = useSearchParams()
    const token = searchParams.get("token")?.trim() || ""
    const [candidateData, setCandidateData] = useState<AnonymousFeedbackCandidateDetail | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!token) {
            setError("Missing feedback token.")
            setIsLoading(false)
            return
        }

        const loadCandidate = async () => {
            try {
                setIsLoading(true)
                setError(null)

                const response = await fetchCandidateByFeedbackToken(token)
                if (response.error) {
                    setError(response.error)
                } else if (response.data) {
                    setCandidateData(response.data)
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to load candidate details."
                setError(message)
            } finally {
                setIsLoading(false)
            }
        }

        loadCandidate()
    }, [token])

    const candidate = useMemo(
        () => (candidateData ? transformCandidateData(candidateData) : null),
        [candidateData]
    )

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8 font-sans flex items-center justify-center">
                <p className="text-sm text-gray-500">Loading candidate details...</p>
            </div>
        )
    }

    if (error || !candidate) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8 font-sans flex items-center justify-center">
                <p className="text-sm text-gray-500">{error || "Candidate not found."}</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8 font-sans">
            <div className="mx-auto max-w-6xl">
                <ProfileHeader
                    activeTab="overview"
                    setActiveTab={() => undefined}
                    candidate={candidate}
                    jobId={null}
                    matchId={null}
                    stage={null}
                    showTabs={false}
                />
                <ProfileOverview
                    candidate={candidate}
                    jobId={null}
                    matchId={null}
                    stage={null}
                    showActions={false}
                />
                <div className="mt-8">
                    <AnonymousFeedbackForm token={token} />
                </div>
            </div>
        </div>
    )
}
